import { Loader } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useState, createContext, useEffect } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { useNavigate } from "react-router-dom";

import { getServiceOrder } from "graphql/queries";

export const OrderContext = createContext({});

export const OrderProvider = ({
  serviceOrderID,
  viewType = "execute",
  fromEquipment = false,
  children,
}) => {
  const navigate = useNavigate();
  const [serviceOrder, setServiceOrder] = useState();
  const [routines, setRoutines] = useState([]);
  const [observations, setObservations] = useState("");

  const onChangeObservations = (e) => {
    const { value } = e.target;
    setObservations(value);
  };

  const onChangeActivity = (
    routineID,
    activityID,
    activityValue,
    equipmentID
  ) => {
    setRoutines((prev) => {
      const listUpdated = [...prev];
      const index = listUpdated.findIndex((it) =>
        equipmentID
          ? it.routineID === routineID && equipmentID === it.equipmentID
          : it.routineID === routineID && !it.equipmentID
      );
      const isNewActivity = listUpdated[index].activities?.some(
        (act) => act.activityID === activityID
      );

      if (!isNewActivity)
        listUpdated[index] = {
          ...listUpdated[index],
          activities: [
            ...listUpdated[index].activities,
            { activityID, activityValue },
          ],
        };
      else
        listUpdated[index] = {
          ...listUpdated[index],
          activities:
            listUpdated[index].activities?.map((act) => {
              if (act.activityID === activityID)
                return {
                  ...act,
                  activityValue,
                };

              return act;
            }) ?? [],
        };

      return listUpdated;
    });
  };

  async function fetchServiceOrder(id) {
    try {
      const serviceOrderData = await API.graphql(
        graphqlOperation(getServiceOrder, { id })
      );
      const serviceOrderItem = serviceOrderData.data.getServiceOrder;
      if (!serviceOrderItem) {
        toast.error(strings.responses.errorFetch);
        navigate("/service-orders");
        return;
      }

      setRoutines([
        ...(serviceOrderItem.equipmentRoutines?.map((t) => ({
          equipmentID: t.equipmentID,
          routineID: t.routineID,
          activities: [],
        })) ?? []),
        ...(serviceOrderItem.maintenanceRoutines?.items?.map((t) => ({
          routineID: t.maintenanceRoutineId,
          activities: [],
        })) ?? []),
      ]);
      setServiceOrder({
        ...serviceOrderItem,
        tools: serviceOrderItem.tools?.items?.map((t) => t.toolId) ?? [],
        spares: serviceOrderItem.spares?.items?.map((t) => t.id) ?? [],
        maintenanceRoutines:
          serviceOrderItem.maintenanceRoutines?.items?.map(
            (t) => t.maintenanceRoutineId
          ) ?? [],
        equipments:
          serviceOrderItem.equipments?.items?.map((t) => t.equipmentId) ?? [],
        equipmentRoutines:
          serviceOrderItem.equipmentRoutines?.map((eqR) => ({
            equipmentID: eqR.equipmentID,
            routineID: eqR.routineID,
          })) ?? [],
        equipmentSpares:
          serviceOrderItem.equipmentSpares?.map((eqS) => ({
            equipmentID: eqS.equipmentID,
            spares: eqS.spares,
          })) ?? [],
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  useEffect(() => {
    fetchServiceOrder(serviceOrderID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOrderID]);

  if (!serviceOrder) return <Loader />;

  const value = {
    viewType,
    serviceOrder,
    fromEquipment,
    routines,
    observations,
    onChangeActivity,
    onChangeObservations,
  };

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};

const strings = new LocalizedStrings({
  es: {
    responses: {
      errorFetch: "Error al consultar la orden de servicio",
    },
  },
});
